import type { InventoryItem } from '../types/inventory'

// Office Plans v0 (pre-validation PR 6, docs/crew-mode/prd/crew-mode-office-plans-v0.md).
//
// A feed mix is a recipe, not stock: an InventoryItem (category 'feed') whose
// `metadata.mix` lists 2+ component feed items with percentages adding up to
// 100. Preparing a mix moves no stock; feeding X kg of it records one FeedLog
// per component (kg = X × percent), so each component's stock and cost come
// from its own item (user decision 2026-09-26).
//
// Deliveries ("Recibir") are kept as history in `metadata.receipts`; the most
// recent one's lot number is stamped on every feed record of that item
// (BAP 3.1 T7, GLOBALG.A.P. AQ-GFS 22.02.01).

export type FeedMixComponent = { itemId: string; percent: number }
export type FeedMix = { components: FeedMixComponent[] }

export type InventoryReceipt = {
  id: string
  date: string
  quantity: number
  supplier?: string
  lotNumber?: string
  unitCostCents?: number
  currency?: string
  expiresOn?: string
}

const isPlainObject = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
)

const PERCENT_TOLERANCE = 0.001

export const readMix = (item: Pick<InventoryItem, 'metadata'> | null | undefined): FeedMix | null => {
  const raw = isPlainObject(item?.metadata) ? item!.metadata!.mix : undefined
  if (!isPlainObject(raw) || !Array.isArray(raw.components)) return null
  const components: FeedMixComponent[] = []
  for (const entry of raw.components) {
    if (!isPlainObject(entry)) continue
    const itemId = typeof entry.itemId === 'string' ? entry.itemId.trim() : ''
    const percent = typeof entry.percent === 'number' ? entry.percent : Number(entry.percent)
    if (!itemId || !Number.isFinite(percent)) continue
    components.push({ itemId, percent })
  }
  return { components }
}

export const isMixItem = (item: Pick<InventoryItem, 'metadata'> | null | undefined): boolean => readMix(item) !== null

export type MixValidation =
  | { ok: true }
  | { ok: false; reason: 'tooFewComponents' | 'duplicateComponent' | 'invalidPercent' | 'sumNot100' | 'unknownComponent' }

/**
 * A mix is feedable only when it has 2+ distinct, existing, non-mix feed
 * components, each with a percent in (0, 100), adding up to 100 (C2).
 */
export const validateMix = (mix: FeedMix | null, feedItems: InventoryItem[]): MixValidation => {
  if (!mix || mix.components.length < 2) return { ok: false, reason: 'tooFewComponents' }
  const ids = new Set<string>()
  for (const component of mix.components) {
    if (ids.has(component.itemId)) return { ok: false, reason: 'duplicateComponent' }
    ids.add(component.itemId)
    if (!(component.percent > 0 && component.percent < 100)) return { ok: false, reason: 'invalidPercent' }
    const item = feedItems.find((candidate) => candidate.id === component.itemId)
    if (!item || item.category !== 'feed' || isMixItem(item)) return { ok: false, reason: 'unknownComponent' }
  }
  const sum = mix.components.reduce((total, component) => total + component.percent, 0)
  if (Math.abs(sum - 100) > PERCENT_TOLERANCE) return { ok: false, reason: 'sumNot100' }
  return { ok: true }
}

const round3 = (value: number): number => Math.round(value * 1000) / 1000

/**
 * Splits a fed total across the mix components: each component gets
 * total × percent / 100 rounded to 3 decimals, and the last one takes the
 * remainder so the parts add up exactly to the total (PRD §10 rounding).
 */
export const splitMixKg = (totalKg: number, mix: FeedMix): Array<FeedMixComponent & { kg: number }> => {
  const parts: Array<FeedMixComponent & { kg: number }> = []
  let assigned = 0
  mix.components.forEach((component, index) => {
    const isLast = index === mix.components.length - 1
    const kg = isLast ? round3(totalKg - assigned) : round3((totalKg * component.percent) / 100)
    assigned = round3(assigned + kg)
    parts.push({ ...component, kg })
  })
  return parts
}

export const readReceipts = (item: Pick<InventoryItem, 'metadata'> | null | undefined): InventoryReceipt[] => {
  const raw = isPlainObject(item?.metadata) ? item!.metadata!.receipts : undefined
  if (!Array.isArray(raw)) return []
  const receipts: InventoryReceipt[] = []
  for (const entry of raw) {
    if (!isPlainObject(entry)) continue
    const date = typeof entry.date === 'string' ? entry.date : ''
    const quantity = typeof entry.quantity === 'number' ? entry.quantity : Number(entry.quantity)
    if (!date || !Number.isFinite(quantity)) continue
    receipts.push({
      id: typeof entry.id === 'string' ? entry.id : `${date}-${receipts.length}`,
      date,
      quantity,
      supplier: typeof entry.supplier === 'string' && entry.supplier.trim() ? entry.supplier.trim() : undefined,
      lotNumber: typeof entry.lotNumber === 'string' && entry.lotNumber.trim() ? entry.lotNumber.trim() : undefined,
      unitCostCents: typeof entry.unitCostCents === 'number' && Number.isFinite(entry.unitCostCents) ? entry.unitCostCents : undefined,
      currency: typeof entry.currency === 'string' && entry.currency.trim() ? entry.currency.trim() : undefined,
      expiresOn: typeof entry.expiresOn === 'string' && entry.expiresOn.trim() ? entry.expiresOn.trim() : undefined
    })
  }
  // Oldest first; stable for equal dates (insertion order).
  return receipts
    .map((receipt, index) => ({ receipt, index }))
    .sort((a, b) => a.receipt.date.localeCompare(b.receipt.date) || a.index - b.index)
    .map(({ receipt }) => receipt)
}

/** The lot number of the most recent delivery that has one. */
export const latestReceiptLotNumber = (item: Pick<InventoryItem, 'metadata'> | null | undefined): string | undefined => {
  const receipts = readReceipts(item)
  for (let index = receipts.length - 1; index >= 0; index -= 1) {
    if (receipts[index].lotNumber) return receipts[index].lotNumber
  }
  return undefined
}

/**
 * Applies a delivery to an item: appends the receipt, adds the quantity to
 * stock, and moves the item's received-on date, unit cost and expiry to the
 * delivery's values when given.
 *
 * `onHand` is the stock counted on `metadata.receivedOn` (Inventario
 * subtracts feed used since that date to estimate today's stock). So when
 * the delivery moves that date forward, the feed used between the old date
 * and the delivery (`usedKgSincePreviousAnchor`) is taken off first; a
 * back-dated delivery (before the current date) only adds its quantity.
 */
export const applyReceipt = (
  item: InventoryItem,
  receipt: InventoryReceipt,
  nowIso: string,
  usedKgSincePreviousAnchor = 0
): InventoryItem => {
  const baseMeta = isPlainObject(item.metadata) ? item.metadata : {}
  const existing = Array.isArray(baseMeta.receipts) ? baseMeta.receipts : []
  const currentReceivedOn = typeof baseMeta.receivedOn === 'string' ? baseMeta.receivedOn : ''
  const movesAnchor = !currentReceivedOn || receipt.date >= currentReceivedOn
  const currentOnHand = Number.isFinite(item.onHand) ? item.onHand : 0
  const base = movesAnchor ? Math.max(0, currentOnHand - Math.max(0, usedKgSincePreviousAnchor)) : currentOnHand
  const nextMeta: Record<string, unknown> = {
    ...baseMeta,
    receipts: [...existing, { ...receipt }],
    receivedOn: movesAnchor ? receipt.date : currentReceivedOn
  }
  if (receipt.expiresOn) nextMeta.expiresOn = receipt.expiresOn
  const onHand = Math.round((base + receipt.quantity) * 1000) / 1000
  return {
    ...item,
    onHand,
    ...(receipt.unitCostCents !== undefined ? { unitCostCents: receipt.unitCostCents } : {}),
    ...(receipt.currency ? { currency: receipt.currency } : {}),
    metadata: nextMeta,
    updatedAt: nowIso
  }
}
